import EventEmitter from '../_util/EventEmitter';
import ImageLayer from '../image-layer/ImageLayer';
import FeatureLayer from '../feature-layer/FeatureLayer';
import { CustomDataSource, ImageryLayer, Primitive } from 'cesium';
import isNumber from 'lodash-es/isNumber';
import isEqual from 'lodash-es/isEqual';
import cloneDeep from 'lodash-es/cloneDeep';
// 获取所有keys
const getAllKeys = data => {
    let keys = [];
    data.map(v => {
        if (v.layers?.length) {
            keys.push(...getAllKeys(v.layers));
        } else {
            keys.push(v.key);
        }
    });

    return keys;
};

// 获取显示或隐藏的keys
const getLayersVisibleKeys = (data, map, visible = true) => {
    let keys = [];
    data.map(v => {
        if (v.key) {
            let layer = map.getLayerByKey(v.key);
            if (layer) {
                if (layer.group?.length) {
                    if (visible) {
                        layer.group.map(f => {
                            if (!f.hasOwnProperty('show') || f.show) {
                                keys.push(f.key);
                            }
                        });
                    } else {
                        layer.group.map(f => {
                            if (!f.show) {
                                keys.push(f.key);
                            }
                        });
                    }
                } else if (layer.show === visible) {
                    keys.push(v.key);
                }
            }
        }
        if (v.layers?.length) {
            let _keys = getLayersVisibleKeys(v.layers, map, visible);
            if (_keys?.length) {
                keys.push(..._keys);
            }
        }
    });

    return keys;
};

// 获取图层
const getLayers = (data, layer, _keys) => {
    const layers = [];

    data.map(v => {
        if (v.key && v instanceof layer) {
            if (_keys) {
                const keys = Array.isArray(_keys) ? _keys : [_keys];
                if (keys.includes(v.key)) {
                    layers.push(v.key);
                }
            } else {
                layers.push(v.key);
            }
        }

        if (v.layers?.length) {
            let _keys = getLayers(v.layers, layer);
            if (_keys?.length) {
                layers.push(..._keys);
            }
        }
    });

    return layers;
};

class Layers extends EventEmitter {
    constructor(options) {
        super();

        this.map = options.map;

        this.layers = [];

        this.highlightFeature = null;
    }

    // 图层改变的时候
    onChange() {
        this.emit('change', this.layers);
    }

    // 图层新增
    addLayer(layer, index) {
        const map = this.map;
        if (layer instanceof CustomDataSource) {
            map.dataSources.add(layer);
        } else if (layer instanceof ImageryLayer) {
            if (index && map.scene.imageryLayers?._layers?.length) {
                map.scene.imageryLayers.add(layer, index);
            } else {
                map.scene.imageryLayers.add(layer);
            }
        } else if (layer instanceof Primitive) {
            if (index) {
                map.scene.primitives.add(layer, index);
            } else {
                map.scene.primitives.add(layer);
            }
        }

        // 图层是否含有name字段，作为加入到图层树的判断依据
        if (layer.name && layer.layerControl) {
            if (isNumber(index)) {
                this.layers.splice(index, 0, layer);
            } else {
                this.layers.push(layer);
            }
            this.onChange();
            this.emit('add', layer);
        }
    }

    // 图层移除
    removeLayer(layer) {
        const map = this.map;

        if (layer instanceof CustomDataSource) {
            map.dataSources.remove(layer);
        } else if (layer instanceof ImageryLayer) {
            map.scene.imageryLayers.remove(layer);
        } else if (layer instanceof Primitive) {
            map.scene.primitives.remove(layer);
        }

        // 图层是否含有name字段，作为加入到图层树的判断依据
        if (layer.name && layer.layerControl) {
            this.layers = this.layers.filter(v => v !== layer);
            this.emit('remove', layer);
            this.onChange();
        }
    }

    // 图层清空
    clearLayers() {
        const map = this.map;
        const imageryLayers = map.getImageryLayers() || [];

        map.dataSources.removeAll();

        imageryLayers.map(layer => {
            if (layer?.key) {
                map.imageryLayers.remove(layer);
            }
        });

        map.entities.removeAll();

        this.layers = [];
        this.emit('clear');
        this.onChange();
    }

    // 获取 layers 里面的 key 集合
    getAllKeys() {
        return getAllKeys(this.layers);
    }

    // 获取当前展示的 keys
    getShowKeys() {
        return getLayersVisibleKeys(this.layers, this.map, true);
    }

    // 获取当前隐藏的 keys
    getHiddenKeys() {
        return getLayersVisibleKeys(this.layers, this.map, false);
    }

    // 获取 ImageLayer 图层
    getImageLayers(_keys) {
        return getLayers(this.layers, ImageLayer, _keys);
    }

    // 获取 FeatureLayer 图层
    getFeatureLayers(_keys) {
        return getLayers(this.layers, FeatureLayer, _keys);
    }

    // 根据key查找imageryLayers图层
    getImageryLayers(_keys) {
        const map = this.map;

        const layers = map?.imageryLayers?._layers || [];

        if (_keys && layers.length) {
            const keys = Array.isArray(_keys) ? _keys : [_keys];

            return layers.filter(layer => layer.key && keys.includes(layer.key));
        }

        return layers;
    }

    // 根据 keys 设置图层的显示，其他的隐藏
    setShowByKeys(_keys) {
        const map = this.map;

        const keys = Array.isArray(_keys) ? _keys : [_keys];

        let layerKey = null;
        if (this.highlightFeature) {
            layerKey = map.getLayerByFeature(this.highlightFeature)?.key;
        }


        let highlightShow = false;
        this.getAllKeys().map(a => {
            let layer = map.getLayerByKey(a);
            if (layer) {
                if (layer.group?.length) {
                    let group = cloneDeep(layer.group);
                    group.map(v => {
                        v.show = _keys.includes(v.key);

                        if (v.show && (layerKey === layer.key)) {
                            highlightShow = true;
                        }
                    });

                    if (!isEqual(group, layer.group)) {
                        layer.setGroup(group);
                    }
                } else {
                    let show = keys.includes(a);
                    if (show !== layer.show) {
                        layer.setVisible(show);

                        if (layerKey === layer.key) {
                            highlightShow = true;
                        }
                    }
                }
            }
        });

        if (!highlightShow) {
            map.hideTemplate();
        }
    }

    // 根据 keys 设置图层的隐藏，其他的显示
    setHideByKeys(_keys) {
        const map = this.map;

        const keys = Array.isArray(_keys) ? _keys : [_keys];

        this.getAllKeys().map(a => {
            let layer = map.getLayerByKey(a);
            if (layer) {
                layer.show = !keys.includes(a);
            }
        });
    }

    getLayerIndex(key) {
        return this.layers.findIndex(layer => layer.key === key);
    }
}

export default Layers;
